import { useEffect, useState } from "react";
import { Avatar, Box, Container, Heading, Image, SimpleGrid, Stack, Text } from "@chakra-ui/react";
import { useCmsApi } from "../../services/useCmsApi";

interface ITestimonialDisplay {
	name: string;
	location: string;
	quote: string;
	headshotUrl?: string;
}

const fallbackTestimonials: ITestimonialDisplay[] = [
	{
		name: "Kelly Boles",
		location: "Saskatchewan, Canada",
		quote:
			"Geco maps showed us exactly where our kochia patches would be before the season even started. We targeted our residual herbicide application and cut our input costs significantly.",
	},
	{
		name: "Jake Leguee",
		location: "Saskatchewan, Canada",
		quote:
			"The prescription maps loaded straight into our equipment. No new technology, no new workflow. Just better decisions about where to spray.",
	},
	{
		name: "John Harrington",
		location: "Alberta, Canada",
		quote:
			"We've been dealing with the same weedy patches for years. After one season with Geco we finally have a plan to actually knock them back instead of just managing them.",
	},
	{
		name: "Matt Coutts",
		location: "Manitoba, Canada",
		quote:
			"Simple to get started. We sent our field boundaries, Geco ran the analysis, and we had results back in a day. Exactly what we needed heading into planting.",
	},
	{
		name: "Ralph Tessman",
		location: "Saskatchewan, Canada",
		quote:
			"The multi-year weed history maps made it obvious which parts of the field were the real problem areas. That's the kind of insight that changes how you manage a farm.",
	},
	{
		name: "Brunel Sabourin",
		location: "Saskatchewan, Canada",
		quote:
			"We used the VR seeding prescription to increase seeding rates in the weediest zones. Added crop competition right where it was needed most.",
	},
	{
		name: "Jamie Christie",
		location: "Alberta, Canada",
		quote:
			"Geco fits into what we're already doing. No new equipment, no new practices. Just better information about our own fields.",
	},
];

const TestimonialCard = ({ name, location, quote, headshotUrl }: ITestimonialDisplay) => (
	<Stack
		bg="white"
		borderRadius="xl"
		boxShadow="sm"
		border="1px solid"
		borderColor="gray.100"
		p="6"
		spacing="4"
		h="full"
	>
		<Text color="gray.600" fontSize="sm" flex="1" fontStyle="italic">
			"{quote}"
		</Text>
		<Stack direction="row" align="center" spacing="3">
			{headshotUrl?.startsWith("http") ? (
				<Image src={headshotUrl} alt={name} boxSize="32px" borderRadius="full" objectFit="cover" />
			) : (
				<Avatar name={name} size="sm" bg="green.600" color="white" />
			)}
			<Stack spacing="0">
				<Text fontWeight="semibold" fontSize="sm" color="gray.800">{name}</Text>
				<Text fontSize="xs" color="gray.500">{location}</Text>
			</Stack>
		</Stack>
	</Stack>
);

export const TestimonialsSection = (): JSX.Element => {
	const cmsApi = useCmsApi();
	const [testimonials, setTestimonials] = useState<ITestimonialDisplay[]>(fallbackTestimonials);

	useEffect(() => {
		let cancelled = false;
		cmsApi.getTestimonials().then((items) => {
			if (!cancelled && items.length > 0) {
				setTestimonials(items.map((t) => ({
					name: t.name,
					location: t.location || "",
					quote: t.quote,
					headshotUrl: t.headshotUrl || undefined,
				})));
			}
		});
		return () => { cancelled = true; };
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Box py={{ base: "16", md: "24" }} bg="gray.50">
			<Container maxW="1480px">
				<Stack spacing="10">
					<Stack spacing="3" textAlign="center" align="center">
						<Heading size={{ base: "sm", md: "xl" }} color="gray.800">
							What Growers Are Saying
						</Heading>
						<Text color="gray.600" fontSize={{ base: "lg", md: "xl" }} maxW="2xl" mx="auto">
							Farmers across the Canadian prairies are using Geco to take control of their weed pressure.
						</Text>
					</Stack>
					<SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="6">
						{testimonials.map((t) => (
							<TestimonialCard key={t.name} {...t} />
						))}
					</SimpleGrid>
				</Stack>
			</Container>
		</Box>
	);
};
